import React from 'react';
import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import cookie from 'cookie';
import Head from 'next/head';
import API from 'utils/ServerSideAPI';
import { WorkoutDetailScreen } from 'screens/WorkoutDetail/WorkoutDetailScreen';
import { WorkoutNotFoundScreen } from 'screens/WorkoutDetail/WorkoutNotFoundScreen';
import { UserLinkPreviewMetadataResponse } from 'hevy-shared';
import { BootstrapLoadingWrapper } from 'components/BootstrapLoadingWrapper';
import { WorkoutDetailPublicScreen } from 'screens/WorkoutDetail/WorkoutDetailPublicScreen';
import { t } from 'translations/t';

import { Language } from 'hevy-shared/translations';
import { browserLanguageFromContext } from 'utils/pageUtils';

type WorkoutDetailProps = {
  urlWorkoutId: string | null;
  metadata: UserLinkPreviewMetadataResponse | null;
  isAuthenticated: boolean;
  browserLanguage: Language;
};

export const getServerSideProps: GetServerSideProps = async (
  context,
): Promise<GetServerSidePropsResult<WorkoutDetailProps>> => {
  const { workoutId: urlWorkoutId } = context.query;
  const rawCookies = context.req?.headers.cookie;
  const cookies = rawCookies ? cookie.parse(rawCookies) : {};

  const isAuthenticated = API.isAuthenticated(cookies);

  if (typeof urlWorkoutId !== 'string') {
    return {
      props: {
        urlWorkoutId: null,
        metadata: null,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  }

  try {
    const result = await API.getWorkoutPreviewMetadata(urlWorkoutId);

    return {
      props: {
        urlWorkoutId,
        metadata: result.data,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  } catch {
    return {
      props: {
        urlWorkoutId,
        metadata: null,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  }
};

export default function WorkoutDetail({
  urlWorkoutId,
  metadata,
  isAuthenticated,
  browserLanguage,
}: WorkoutDetailProps) {
  return (
    <>
      {metadata && (
        <Head>
          <title>{metadata.title}</title>
          <meta
            name="description"
            content={t('web.metadata.workout.desc', {
              username: metadata.username,
            })}
          />
          <meta
            name="robots"
            content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          />
          <link rel="canonical" href={metadata.url} />
          <meta property="og:locale" content={browserLanguage} />
          <meta property="og:locale:alternate" content="ar" />
          <meta property="og:locale:alternate" content="ca" />
          <meta property="og:locale:alternate" content="de" />
          <meta property="og:locale:alternate" content="en" />
          <meta property="og:locale:alternate" content="es" />
          <meta property="og:locale:alternate" content="fr" />
          <meta property="og:locale:alternate" content="hi" />
          <meta property="og:locale:alternate" content="it" />
          <meta property="og:locale:alternate" content="ja" />
          <meta property="og:locale:alternate" content="ko" />
          <meta property="og:locale:alternate" content="pl" />
          <meta property="og:locale:alternate" content="pt_BR" />
          <meta property="og:locale:alternate" content="pt_PT" />
          <meta property="og:locale:alternate" content="ru" />
          <meta property="og:locale:alternate" content="tr" />
          <meta property="og:locale:alternate" content="zh_CN" />
          <meta property="og:locale:alternate" content="zh_TW" />
          <meta name="og:type" content="website" />
          <meta property="og:title" content={metadata.title} />
          <meta
            property="og:description"
            content={t('web.metadata.workout.desc', {
              username: metadata.username,
            })}
          />
          <meta property="og:url" content={metadata.url} />
          <meta property="og:site_name" content={t('web.metadata.socialWorkoutTracker')} />
          <meta property="og:image" content={metadata.image_url} />
          <meta property="og:image:width" content="400" />
          <meta property="og:image:height" content="400" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:creator" content="@hevyapp" />
          <meta name="twitter:site" content="@hevyapp" />
          <meta name="twitter:title" content={metadata.title} />
          <meta
            name="twitter:description"
            content={t('web.metadata.workout.desc', {
              username: metadata.username,
            })}
          />
          <meta name="twitter:image" content={metadata.twitter_image_url} />
          <meta name="twitter:label1" content={t('web.metadata.user')} />
          <meta name="twitter:data1" content={metadata.username} />
        </Head>
      )}
      <BootstrapLoadingWrapper>
        {urlWorkoutId ? (
          isAuthenticated ? (
            <WorkoutDetailScreen urlWorkoutId={urlWorkoutId} />
          ) : (
            <WorkoutDetailPublicScreen urlWorkoutId={urlWorkoutId} />
          )
        ) : (
          <WorkoutNotFoundScreen />
        )}
      </BootstrapLoadingWrapper>
    </>
  );
}
